import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSignalRContext } from '../contexts/SignalRContext.tsx';
import { parseSignalRError } from '../utils/helpers.ts';
import Modal from '../components/common/Modal.tsx';

const RoomPage: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { connection, isConnected } = useSignalRContext();
  const [players, setPlayers] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isHost, setIsHost] = useState(false);
  const [gameStarting, setGameStarting] = useState(false);
  const [joined, setJoined] = useState(false);
  const [showHomeModal, setShowHomeModal] = useState(false);
  const isCreator = location.state?.isCreator || false;

  // Username: from location.state or localStorage
  const username = location.state?.username || localStorage.getItem('sos-username') || '';

  // If username is missing, redirect to HomePage
  useEffect(() => {
    if (!username) {
      navigate('/');
    }
  }, [username, navigate]);

  // Odaya katılma işlemi (username ile)
  useEffect(() => {
    if (!joined && connection && isConnected && roomId && username && !isCreator) {
      setJoined(true);
      const joinRoom = async () => {
        try {
          await connection.invoke('JoinRoom', roomId, username);
        } catch (err: any) {
          setError(parseSignalRError(err));
          setTimeout(() => navigate('/'), 3000);
        }
      };
      joinRoom();
    }
    // Reconnect olursa tekrar JoinRoom çağır
    if (connection) {
      connection.onreconnected(() => {
        if (roomId && username && !isCreator) {
          connection.invoke('JoinRoom', roomId, username).catch(() => {});
        }
      });
    }
  }, [connection, isConnected, roomId, username, isCreator, joined, navigate]);

  // Eğer oyuncu listesi hala boşsa, manuel çek
  useEffect(() => {
    if (!connection || !roomId || !username || isCreator) return;

    const fetchPlayerList = async () => {
      try {
        const players = await connection.invoke('GetPlayerList', roomId);
        setPlayers(players);
      } catch (err) {
        // ignore
      }
    };

    if (players.length === 0) {
      fetchPlayerList();
    }
  }, [connection, roomId, username, isCreator, players.length]);

  // Eğer creator isen ve players listesi boşsa, kendi ismini geçici olarak göster
  const displayPlayers = players.length === 0 && isCreator ? [username] : players;

  // PlayerListUpdated eventini her zaman dinle
  useEffect(() => {
    if (!connection || !roomId) return;

    const handlePlayerListUpdated = (players: string[]) => {
      setPlayers(players);
      if (players.length > 0 && players[0] === username) {
        setIsHost(true);
      } else {
        setIsHost(false);
      }
    };

    connection.on('PlayerListUpdated', handlePlayerListUpdated);

    // Only navigate to GamePage via GameStarted event
    connection.on('GameStarted', () => {
      setGameStarting(false);
      localStorage.setItem('sos-username', username);
      navigate(`/game/${roomId}`, { state: { username } });
    });

    return () => {
      connection.off('PlayerListUpdated', handlePlayerListUpdated);
      connection.off('GameStarted');
    };
  }, [connection, roomId, navigate, username]);

  const handleStartGame = async () => {
    if (!connection || !roomId) return;
    setGameStarting(true);
    try {
      await connection.invoke('StartNewGame', roomId);
    } catch (err) {
      setError(parseSignalRError(err));
      setGameStarting(false);
    }
  };

  const handleGoHome = () => {
    // Odayı terk et ve ana sayfaya git
    if (connection && roomId) {
      try {
        // Odayı terk et (opsiyonel - backend'de LeaveRoom varsa)
        connection.invoke('LeaveRoom', roomId).catch(() => {
          // Hata olursa yok say
        });
      } catch (err) {
        // Hata yönetimi
      }
    }
    // Ana sayfaya git
    navigate('/');
  };

  if (!isConnected) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800">Connecting...</h2>
          <p className="text-gray-600">Please wait while we connect to the game server.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Go to Home Modal */}
      <Modal isOpen={showHomeModal} onClose={() => setShowHomeModal(false)} title="Return to Home">
        <div className="flex flex-col items-center gap-4">
          <p className="text-gray-700 text-center">
            Are you sure you want to return to the home page? You will leave the room.
          </p>
          <div className="flex gap-3">
            <button
              className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition"
              onClick={() => setShowHomeModal(false)}
            >
              No
            </button>
            <button
              className="px-6 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
              onClick={handleGoHome}
            >
              Yes
            </button>
          </div>
        </div>
      </Modal>

      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-4">
        <div className="bg-white p-6 sm:p-8 rounded-lg shadow-md w-full max-w-md mb-6">
          <div className="flex items-center justify-center gap-2 sm:gap-3 mb-6">
            <img 
              src="/logo192.png" 
              alt="SOS Game Logo" 
              className="w-8 h-8 sm:w-10 sm:h-10 object-contain cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => setShowHomeModal(true)}
            />
            <h1 className="text-2xl sm:text-3xl font-bold">Game Room</h1>
          </div>
        <div className="mb-4 text-center">
          <span className="text-gray-600">Room ID: </span>
          <span className="font-mono font-bold text-lg tracking-widest bg-gray-100 px-2 py-1 rounded select-all">{roomId}</span>
        </div>
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-2">Players ({players.length}/4)</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {displayPlayers.map((player, index) => (
              <div key={index} className={`bg-gray-50 p-3 rounded-lg ${player === username ? 'font-bold text-blue-600 border-2 border-blue-300' : 'text-gray-700'}`}>
                <div className="flex items-center justify-between">
                  <span className="truncate">{player}</span>
                  <div className="flex gap-1">
                    {index === 0 && <span className="text-xs text-purple-500 bg-purple-100 px-1 rounded">Host</span>}
                    {player === username && <span className="text-xs text-green-500 bg-green-100 px-1 rounded">You</span>}
                  </div>
                </div>
              </div>
            ))}
            {/* Boş slotları göster */}
            {Array.from({ length: 4 - displayPlayers.length }, (_, index) => (
              <div key={`empty-${index}`} className="bg-gray-100 p-3 rounded-lg border-2 border-dashed border-gray-300 text-gray-400 text-center">
                Waiting for player...
              </div>
            ))}
          </div>
        </div>
        {error && (
          <div className="text-red-500 text-sm mb-4">{error}</div>
        )}
        {isHost && players.length >= 2 ? (
          <button
            onClick={handleStartGame}
            disabled={gameStarting}
            className="w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 py-2 rounded-md hover:from-blue-600 hover:to-purple-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 font-semibold text-lg mb-2"
          >
            {gameStarting ? 'Starting...' : `Start Game (${players.length}/4)`}
          </button>
        ) : (
          <button
            disabled
            className="w-full bg-gray-200 text-gray-500 px-4 py-2 rounded-md font-semibold text-lg cursor-not-allowed"
          >
            {players.length < 2 ? `Waiting for more players... (${players.length}/4)` : `Waiting for host to start... (${players.length}/4)`}
          </button>
        )}
        </div>

        {/* Footer */}
        <div className="w-full max-w-md flex flex-col items-center gap-2 text-xs text-gray-400">
          <div>Made with <span className="text-pink-500">♥</span> for your special game night!</div>
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
            <a
              href="mailto:info@playsos.online"
              className="underline hover:text-blue-600 transition flex items-center gap-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline-block text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              info@playsos.online
            </a>
            <a
              href="https://buymeacoffee.com/metinhan"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-amber-600 transition flex items-center gap-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline-block text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Support me
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default RoomPage; 